// Copyright ©️ 2025 eVolve MEP, LLC

import { Alert } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { isKeyOf } from 'helpers/isKeyOf';
import { isNotNil } from 'helpers/isNotNil';

export type LoginErrorCode =
  | 'AuthorizationCodeInvalidOrExpired'
  | 'EmailAlreadyExists'
  | 'EmailNotVerified'
  | 'EulaTermsNotAccepted'
  | 'InvitationExpired'
  | 'LimitExceededException'
  | 'NotAuthorizedException'
  | 'UserNotFoundException' // Catch-all, frontend only
  | 'UserNotProvisioned' // SSO accepted the person, but they have no EVOLVE account
  | 'UnknownException'
  | 'UserNotVerifiedFoundException';

// These codes are essentially unused; we just display whatever error the BE returns.
const loginErrorCopy: Record<LoginErrorCode, string> = {
  AuthorizationCodeInvalidOrExpired: 'Your authorization code is invalid or expired.',
  EmailAlreadyExists: 'Email already exists.',
  EmailNotVerified: 'Your email address has not been verified!',
  EulaTermsNotAccepted: 'You cannot proceed without accepting our EULA terms.',
  InvitationExpired: 'Sorry, this invitation has expired.',
  LimitExceededException: 'Too many attempts, try again later.',
  NotAuthorizedException: 'Invalid email or password.',
  UnknownException: 'Something went wrong. Please try again later.',
  UserNotFoundException: 'Invalid email or password.',
  UserNotProvisioned: 'There is no EVOLVE account for this sign-in. Ask your company administrator to add you.',
  UserNotVerifiedFoundException: '',
};

type Props = {
  errorCode: LoginErrorCode | string | undefined | null;
  onClose?: () => void;
};

export const LoginErrorAlert = ({ errorCode, onClose }: Props) =>
  errorCode ? (
    <Alert
      variant="light-mantine"
      color="red"
      withCloseButton={isNotNil(onClose)}
      icon={<EvolveIcon icon="Info" color="red" />}
      onClose={onClose}
    >
      {isKeyOf(errorCode, loginErrorCopy) ? loginErrorCopy[errorCode] : errorCode}
    </Alert>
  ) : null;
